import React, { useEffect, useState, useCallback } from 'react';
import { useMondayContext } from '../../contexts/monday-context';
import { ordersAPI, templatesAPI } from '../../api';
import { mondayNotice } from '../../services/monday-service';
import TemplateComponents from '../template-components';
import noticeTypes from '../../constants/notice-types';
import './template-component.scss';
import useBoardSelectOptions from '../../hooks/use-board-select/use-board-select-options';
import { useOrderContext } from '../../contexts/order-context';
import { useSettings } from '../../contexts/settings-context';
import useCanFeatures from "../../hooks/use-can-features";

const TEMPLATE_DEFAULT_STATE = {
  data: {},
};

export default function Template({ previewMode, downloadableRef }) {
  const {
    sessionToken,
    mondayContext: { boardId, itemId },
    userData: { accountId, userId },
  } = useMondayContext();
  const [template, setTemplate] = useState(TEMPLATE_DEFAULT_STATE);
  const { boardSelectOptions } = useBoardSelectOptions();
  const { order, setOrder } = useOrderContext();
  const { settings } = useSettings();
  const { canRemoveDocumentBranding } = useCanFeatures();
  const removeBrand = canRemoveDocumentBranding && !!settings?.removeBrand

  useEffect(() => {
    let isSubscribed = true;

    const getTemplateByBoard = async () => {
      try {
        const { template: fetchedTemplate } = await templatesAPI.fetchTemplateByBoard({
          sessionToken,
          boardId,
        });

        if (!isSubscribed) return;
        setTemplate(fetchedTemplate);
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: e.message });
      }
    };

    getTemplateByBoard();

    return () => (isSubscribed = false);
  }, [boardId, sessionToken, setTemplate]);

  useEffect(() => {
    let isSubscribed = true;

    const getOrder = async () => {
      try {
        const { order: fetchedOrder } = await ordersAPI.fetchOrder({
          templateId: template.id,
          sessionToken,
          itemId,
          boardId,
        });

        if (!isSubscribed) return;
        setOrder(fetchedOrder);
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: e.message });
      }
    };

    template.id && getOrder();

    return () => (isSubscribed = false);
  }, [itemId, boardId, sessionToken, template.id, setOrder]);

  const onTemplateDataChange = useCallback(
    async (id, data) => {
      const mergedData = { ...template.data, [id]: data };
      setTemplate({ ...template, data: mergedData });
      try {
        await templatesAPI.saveTemplateData({
          sessionToken,
          templateId: template.id,
          data: mergedData,
        });
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: e.message });
      }
    },
    [template, sessionToken]
  );

  const onOrderDataChange = useCallback(
    async (componentId, newDate) => {
      const { id, orderNumber, orderDate, data } = order;
      const mergedData = { ...data, [componentId]: newDate };
      setOrder({ ...order, data: mergedData });
      try {
        await ordersAPI.saveOrder({
          sessionToken,
          orderId: id,
          orderNumber,
          orderDate,
          data: mergedData,
        });
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: e.message });
      }
    },
    [order, sessionToken, setOrder]
  );

  const onOrderDetailsChange = useCallback(
    async (field, value) => {
      const updatedOrder = { ...order, [field]: value };
      setOrder(updatedOrder);
      const { id, orderNumber, orderDate, data } = updatedOrder;
      try {
        await ordersAPI.saveOrder({
          sessionToken,
          orderId: id,
          orderNumber,
          orderDate,
          data,
        });
      } catch (e) {
        mondayNotice({ type: noticeTypes.ERROR, message: e.message });
      }
    },
    [order, sessionToken, setOrder]
  );

  const renderTemplateComponents = (isPreview) => {
    if (!template.template || !order.id) return;

    return template.template.components.map(({ name, defaultProps, id }) => {
      const Component = TemplateComponents[name];
      const data = template.data[id];
      const orderData = order.data[id];
      const { orderDate, orderNumber } = order;

      return (
        <Component
          key={id}
          id={id}
          {...defaultProps}
          boardSelectOptions={boardSelectOptions}
          data={data || {}}
          orderData={orderData || {}}
          onTemplateDataChange={onTemplateDataChange}
          onOrderDataChange={onOrderDataChange}
          onOrderDetailsChange={onOrderDetailsChange}
          orderDate={orderDate}
          orderNumber={orderNumber}
          previewMode={isPreview}
          sessionToken={sessionToken}
          boardId={boardId}
          itemId={itemId}
          accountId={accountId}
          userId={userId}
        />
      );
    });
  };

  if (!template.template || !order.id) return <></>;

  return (
    <div className="template-component">
      <div className="template">
        {renderTemplateComponents(previewMode)}
        {previewMode && !removeBrand && (
          <img className="powered-by" src="/assets/powered-by/powered-by.png" alt="Powered By monday.com" />
        )}
      </div>
      <div className="dowloadable-template" ref={downloadableRef}>
        {renderTemplateComponents(true)}
      </div>
    </div>
  );
}
